// src/pages/Propietarios.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaUserTie, FaChevronLeft, FaSearch, FaPhone, FaEnvelope, FaHome, FaUsers, FaExclamationTriangle } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import './DepartamentosDetalle.css';

const Propietarios = () => {
    const { fraccionamientoId } = useAuth();
    const [propietarios, setPropietarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (!fraccionamientoId) return;

        const fetchPropietarios = async () => {
            try {
                setLoading(true);
                const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
                const q = query(casasRef, orderBy('numero'));
                const snap = await getDocs(q);

                const data = [];
                snap.forEach((doc) => {
                    const casa = { id: doc.id, ...doc.data() };
                    const perfil = String(casa.perfil || '').trim().toLowerCase();
                    
                    // Filtrar solo propietarios (excluir caseta, guardia, security, arrendador)
                    if (perfil === 'propietario' || perfil === 'propietario/residente' || perfil === 'dueño' || perfil === 'owner') {
                        data.push({
                            id: casa.id,
                            numero: casa.numero || casa.numeroCasa || casa.lote || '—',
                            nombre: casa.nombre_residente || casa.nombreCompleto || casa.nombre || 'Sin registro',
                            telefono: casa.telefono || casa.telefonoPrincipal || '',
                            telefono2: casa.telefono2 || casa.telefonoAlternativo || '',
                            email: casa.correo || casa.email || '',
                            num_residentes: casa.num_residentes || casa.numResidentes || casa.ocupantes || 0,
                            emergencia: casa.emergencia || {},
                            perfil: casa.perfil || 'No especificado',
                        });
                    }
                });

                setPropietarios(data);
            } catch (error) {
                console.error('Error cargando propietarios:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchPropietarios();
    }, [fraccionamientoId]);

    const filteredPropietarios = propietarios.filter((prop) => {
        const term = searchTerm.toLowerCase();
        return (
            String(prop.numero).toLowerCase().includes(term) ||
            prop.nombre.toLowerCase().includes(term) ||
            prop.email.toLowerCase().includes(term) ||
            prop.telefono.toLowerCase().includes(term)
        );
    });

    return (
        <div className="departamentos-page">
            <div className="departamentos-container">
                <header className="departamentos-header">
                    <div className="departamentos-header-icon">
                        <FaUserTie />
                    </div>
                    <div>
                        <h1>Propietarios registrados</h1>
                        <p>Consulta el padrón de propietarios aunque no residan en el fraccionamiento.</p>
                    </div>
                    <Link to="/administracion/departamentos/identificacion" className="departamentos-back-link">
                        <FaChevronLeft /> Regresar
                    </Link>
                </header>

                <section className="departamentos-content">
                    <div className="search-bar-container">
                        <div className="search-bar">
                            <FaSearch />
                            <input
                                type="text"
                                placeholder="Buscar por casa, nombre, email o teléfono..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="search-results-count">
                            {filteredPropietarios.length} {filteredPropietarios.length === 1 ? 'propietario encontrado' : 'propietarios encontrados'}
                        </div>
                    </div>

                    {loading ? (
                        <div className="loading-state">Cargando propietarios...</div>
                    ) : filteredPropietarios.length === 0 ? (
                        <div className="empty-state">
                            <FaUserTie size={48} color="#cbd5e1" />
                            <p>No se encontraron propietarios</p>
                        </div>
                    ) : (
                        <div className="residents-grid">
                            {filteredPropietarios.map((prop) => (
                                <article key={prop.id} className="resident-card">
                                    <div className="resident-card-header">
                                        <div className="resident-avatar">{String(prop.numero || '?').slice(0, 3)}</div>
                                        <div>
                                            <h3 className="resident-name">{prop.nombre}</h3>
                                            <span className="resident-casa"><FaHome /> Casa {prop.numero}</span>
                                        </div>
                                    </div>
                                    <div className="resident-card-body">
                                        <div className="resident-info-row">
                                            <FaPhone color="#6366f1" />
                                            <div>
                                                <span className="info-label">Teléfono</span>
                                                <span className="info-value">{prop.telefono || '—'}</span>
                                            </div>
                                        </div>
                                        {prop.telefono2 && (
                                            <div className="resident-info-row">
                                                <FaPhone color="#6366f1" />
                                                <div>
                                                    <span className="info-label">Teléfono 2</span>
                                                    <span className="info-value">{prop.telefono2}</span>
                                                </div>
                                            </div>
                                        )}
                                        <div className="resident-info-row">
                                            <FaEnvelope color="#6366f1" />
                                            <div>
                                                <span className="info-label">Email</span>
                                                <span className="info-value">{prop.email || '—'}</span>
                                            </div>
                                        </div>
                                        <div className="resident-info-row">
                                            <FaUsers color="#6366f1" />
                                            <div>
                                                <span className="info-label">Residentes</span>
                                                <span className="info-value">{prop.num_residentes || 0}</span>
                                            </div>
                                        </div>
                                        {prop.emergencia?.nombre && (
                                            <div className="resident-info-row emergency-contact">
                                                <FaExclamationTriangle color="#ef4444" />
                                                <div>
                                                    <span className="info-label">Contacto de emergencia</span>
                                                    <span className="info-value">{prop.emergencia.nombre} {prop.emergencia.telefono ? `• ${prop.emergencia.telefono}` : ''}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};

export default Propietarios;
